export function formatCentsUsd(cents: number | null): string | null {
  if (cents == null) {
    return null;
  }

  const dollars = cents / 100;

  return dollars.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
  });
}

export function formatPhoneDisplay(e164: string | null): string | null {
  if (!e164) {
    return null;
  }

  const match = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164);

  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }

  return e164;
}

const shortMonthNames = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** Render dates with the month spelled out. A numeric "10/9/1990" reads as
 *  October 9 to a US clinic and as 10 September to everyone else, and that
 *  ambiguity is invisible until a patient fails identity verification on the
 *  phone — which is exactly how a wrong date of birth reached production. */
export function formatDateWithMonthName(isoDate: string | null): string | null {
  const match = isoDate ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate) : null;

  if (!match) {
    return null;
  }

  const monthName = shortMonthNames[Number(match[2]) - 1];

  if (!monthName) {
    return null;
  }

  return `${monthName} ${Number(match[3])}, ${match[1]}`;
}

export function formatCaseTime(time: string | null): string | null {
  const match = time ? /^(\d{2}):(\d{2})$/.exec(time) : null;

  if (!match) {
    return null;
  }

  const hour = Number(match[1]);
  const suffix = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 === 0 ? 12 : hour % 12;

  return `${displayHour}:${match[2]} ${suffix}`;
}

export function formatHeightText(heightInches: number | null): string | null {
  if (heightInches == null || heightInches <= 0) {
    return null;
  }

  const feet = Math.floor(heightInches / 12);
  const inches = heightInches % 12;

  return `${feet} ft ${inches} in`;
}

export function formatWeightText(weightPounds: number | null): string | null {
  if (weightPounds == null || weightPounds <= 0) {
    return null;
  }

  const kilograms = Math.round(weightPounds * 0.45359237 * 10) / 10;

  return `${kilograms} kg ${weightPounds} lb`;
}

export function formatSexText(sex: "male" | "female" | null): string | null {
  if (sex == null) {
    return null;
  }

  return sex === "male" ? "Male" : "Female";
}

export function formatYesNo(value: boolean | null): string | null {
  if (value == null) {
    return null;
  }

  return value ? "Yes" : "No";
}

export function formatProvenanceTimestamp(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(iso);

  if (!match) {
    return iso;
  }

  const yearShort = match[1]!.slice(2);

  return `${Number(match[2])}/${Number(match[3])}/${yearShort} ${match[4]}:${match[5]}`;
}
